import { useState, useRef, useEffect } from 'react'
import { ChevronRight, ChevronDown, UserPlus, Plus, ShieldOff, Trash2, Search } from 'lucide-react'
import {
  useStandardsAuthorities,
  useStandardsAccess,
  useAddStandardsUser,
  useRemoveStandardsUser,
  useSetStandardsAuthorities,
} from '@/hooks/useStandardsAccess'
import { useAdminPubAuthorities } from '@/hooks/useAdminDatabase'
import type { StandardsAuthority, StandardsUserEntry } from '@/api/adminService'
import LoadingSpinner from '@/components/admin/shared/LoadingSpinner'

export default function StandardsAccessPage() {
  const { data: authorities, isLoading: loadingAuthorities } = useStandardsAuthorities()
  const { data: accessMap, isLoading: loadingAccess } = useStandardsAccess()
  const { data: allPubAuthorities, isLoading: loadingPubAuth } = useAdminPubAuthorities()
  const { mutate: addUser, isPending: isAdding } = useAddStandardsUser()
  const { mutate: removeUser } = useRemoveStandardsUser()
  const { mutate: setAuthorities, isPending: isSavingAuthorities } = useSetStandardsAuthorities()

  const [userInputs, setUserInputs] = useState<Record<string, string>>({})
  const [emailInputs, setEmailInputs] = useState<Record<string, string>>({})
  const [selectedUuid, setSelectedUuid] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLDivElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (dropdownOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      })
    }
  }, [dropdownOpen])

  if (loadingAuthorities || loadingAccess || loadingPubAuth) return <LoadingSpinner />

  const restrictedUuids = new Set((authorities ?? []).map((a: StandardsAuthority) => a.uuid))
  const availableToAdd = (allPubAuthorities ?? []).filter((pa) => !restrictedUuids.has(pa.uuid))
  const pubAuthMeta = new Map((allPubAuthorities ?? []).map((pa) => [pa.uuid, { name: pa.name, code: pa.code }]))

  const toggleExpanded = (uuid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(uuid) ? next.delete(uuid) : next.add(uuid)
      return next
    })
  }

  const handleAddUser = (authorityUuid: string) => {
    const userId = userInputs[authorityUuid]?.trim()
    if (!userId) return
    const email = emailInputs[authorityUuid]?.trim() || null
    addUser({ authorityUuid, userId, email })
    setUserInputs((s) => ({ ...s, [authorityUuid]: '' }))
    setEmailInputs((s) => ({ ...s, [authorityUuid]: '' }))
  }

  const handleAddAuthority = () => {
    if (!selectedUuid) return
    const meta = pubAuthMeta.get(selectedUuid)
    if (!meta) return
    const next: StandardsAuthority[] = [
      ...(authorities ?? []),
      { uuid: selectedUuid, code: meta.code, name: meta.name },
    ]
    setAuthorities(next)
    setSelectedUuid('')
    setSearchQuery('')
  }

  const handleRemoveAuthority = (uuid: string) => {
    const next = (authorities ?? []).filter((a: StandardsAuthority) => a.uuid !== uuid)
    setAuthorities(next)
    setExpanded((prev) => { const s = new Set(prev); s.delete(uuid); return s })
  }

  return (
    <div className="space-y-6">

      {/* ── Section 1: Manage restricted authorities ─────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Restricted Standards Authorities
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Authorities in this list are treated as standards across the entire system — ingestion processes them as standards, the weekly pipeline fetches their updates, and user access is restricted to those explicitly listed.
          </p>
        </div>

        {/* Add authority row */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
          <div ref={searchRef} className="relative flex-1">
            <div ref={inputRef} className="flex items-center border border-gray-300 rounded px-2 py-1.5 gap-2 focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
              <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setDropdownOpen(true); setSelectedUuid('') }}
                onFocus={() => setDropdownOpen(true)}
                placeholder="Search publishing authorities to restrict..."
                className="flex-1 text-sm text-gray-700 outline-none ring-0 border-none bg-transparent focus:outline-none focus:ring-0"
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setSelectedUuid(''); setDropdownOpen(false) }} className="text-gray-400 hover:text-gray-600">
                  <Plus className="w-3 h-3 rotate-45" />
                </button>
              )}
            </div>
            {dropdownOpen && (
              <div style={dropdownStyle} className="bg-white border border-gray-200 rounded shadow-lg max-h-80 overflow-y-auto">
                {availableToAdd
                  .filter((pa) =>
                    !searchQuery ||
                    pa.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    pa.code.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((pa) => (
                    <button
                      key={pa.uuid}
                      onClick={() => { setSelectedUuid(pa.uuid); setSearchQuery(`${pa.name} (${pa.code})`); setDropdownOpen(false) }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${selectedUuid === pa.uuid ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                    >
                      <span className="font-medium">{pa.name}</span>
                      <span className="text-gray-400 ml-1 text-xs">({pa.code})</span>
                    </button>
                  ))
                }
                {availableToAdd.filter((pa) =>
                  !searchQuery ||
                  pa.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  pa.code.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-400">No authorities found</div>
                )}
              </div>
            )}
          </div>
          <button
            disabled={!selectedUuid || isSavingAuthorities}
            onClick={handleAddAuthority}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 transition-colors whitespace-nowrap"
          >
            <Plus className="w-3.5 h-3.5" />
            Add to restricted
          </button>
        </div>

        {/* Restricted authorities list */}
        {!authorities?.length ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">
            No authorities restricted yet. Add one above.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <th className="px-4 py-3">Authority</th>
                <th className="px-4 py-3 w-24 text-center">Users</th>
                <th className="px-4 py-3 w-24">Remove</th>
              </tr>
            </thead>
            <tbody>
              {(authorities ?? []).map(({ uuid }: StandardsAuthority) => {
                const meta = pubAuthMeta.get(uuid)
                const users: StandardsUserEntry[] = accessMap?.[uuid] ?? []
                const isExpanded = expanded.has(uuid)

                return (
                  <>
                    <tr
                      key={uuid}
                      className="hover:bg-gray-50 border-b border-gray-100 cursor-pointer"
                      onClick={() => toggleExpanded(uuid)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                            : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                          }
                          <div>
                            <p className="font-medium text-gray-800">{meta?.name ?? uuid}</p>
                            {meta?.code && <p className="text-xs text-gray-400 font-mono">{meta.code}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                          {users.length}
                        </span>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleRemoveAuthority(uuid)}
                          disabled={isSavingAuthorities}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-40 transition-colors"
                        >
                          <ShieldOff className="w-3 h-3" />
                          Remove
                        </button>
                      </td>
                    </tr>

                    {/* Expanded sub-table */}
                    {isExpanded && (
                      <tr key={`${uuid}-expanded`} className="bg-gray-50 border-b border-gray-100">
                        <td colSpan={3} className="px-8 py-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-gray-400 uppercase tracking-wider border-b border-gray-200">
                                <th className="pb-2 font-medium">User ID</th>
                                <th className="pb-2 font-medium">Email</th>
                                <th className="pb-2 w-12"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {users.length === 0 ? (
                                <tr>
                                  <td colSpan={3} className="py-3 text-gray-300 text-center">
                                    No users — all users are blocked
                                  </td>
                                </tr>
                              ) : (
                                users.map((entry) => (
                                  <tr key={entry.user_id} className="hover:bg-gray-100">
                                    <td className="py-2 font-mono text-gray-700">{entry.user_id}</td>
                                    <td className="py-2 text-gray-500">{entry.email ?? <span className="text-gray-300">—</span>}</td>
                                    <td className="py-2">
                                      <button
                                        onClick={() => removeUser({ authorityUuid: uuid, userId: entry.user_id })}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                        aria-label={`Remove ${entry.user_id}`}
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                              {/* Add user row */}
                              <tr>
                                <td className="pt-3">
                                  <input
                                    type="text"
                                    value={userInputs[uuid] ?? ''}
                                    onChange={(e) => setUserInputs((s) => ({ ...s, [uuid]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddUser(uuid) }}
                                    placeholder="user_id"
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="pt-3 pl-2">
                                  <input
                                    type="text"
                                    value={emailInputs[uuid] ?? ''}
                                    onChange={(e) => setEmailInputs((s) => ({ ...s, [uuid]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddUser(uuid) }}
                                    placeholder="email (optional)"
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="pt-3 pl-2">
                                  <button
                                    disabled={!userInputs[uuid]?.trim() || isAdding}
                                    onClick={() => handleAddUser(uuid)}
                                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 transition-colors whitespace-nowrap"
                                  >
                                    <UserPlus className="w-3 h-3" />
                                    Add
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
